import { useAppDispatch, useAppSelector } from '../store/store'
import {
  startSignin,
  chooseMethod,
  signupSubmit,
  signinSubmit,
  signinWithPasskey,
  nextStepAction,
  previousStepAction,
  setError,
} from '../store/index'

// Simple utility - no hooks, no recreation
const createRecaptchaHandler = <T extends any[]>(
  dispatch: ReturnType<typeof useAppDispatch>,
  actionCreator: (recaptcha: string, ...args: T) => any,
  logName: string,
) => {
  return async (recaptchaPromise: Promise<string>, ...args: T) => {
    try {
      const recaptcha = await recaptchaPromise
      dispatch(actionCreator(recaptcha, ...args))
    } catch (error: any) {
      dispatch(setError(error.message))
    }
  }
}

// Actions that need recaptcha + error handling
export const useStartSignin = () => {
  const dispatch = useAppDispatch()
  return createRecaptchaHandler(
    dispatch,
    (recaptcha: string, email: string) => startSignin({ recaptcha, email }),
    'startSignin',
  )
}

export const useSignupSubmit = () => {
  const dispatch = useAppDispatch()

  return async (passkey: string | null = null) => {
    try {
      dispatch(signupSubmit({ passkey }))
    } catch (error: any) {
      dispatch(setError(error.message))
    }
  }
}

export const useSigninWithPasskey = () => {
  const dispatch = useAppDispatch()

  return async () => {
    try {
      dispatch(signinWithPasskey())
    } catch (error: any) {
      dispatch(setError(error.message))
    }
  }
}

export const useSigninSubmit = () => {
  const dispatch = useAppDispatch()

  return async (authValue: any, handleErrors = true) => {
    try {
      dispatch(signinSubmit({ authValue, handleErrors }))
    } catch (error: any) {
      if (handleErrors) {
        dispatch(setError(error.message))
      } else {
        throw error
      }
    }
  }
}

// Simple actions without recaptcha
export const useRetryMethod = () => {
  const dispatch = useAppDispatch()
  const signinData = useAppSelector((state) => state.auth.signin)

  return async () => {
    if (signinData?.authMethod?.type) {
      dispatch(chooseMethod(signinData.authMethod.type))
    }
  }
}

export const useAuthNavigation = () => {
  const dispatch = useAppDispatch()

  return {
    nextStep: () => {
      dispatch(nextStepAction())
    },
    previousStep: () => {
      dispatch(previousStepAction())
    },
  }
}

export { useAppDispatch } from '../store/store'
